import { useState } from 'react';
import { useTranslations } from '@vp/om-translations';
import { Button, FlexBox, Link, Span, Spinner, VisuallyHidden } from '@vp/swan';
import { useSourcePageContext } from '../../contexts/SourcePageContext';
import { useSiteData } from '../../../hooks/useSiteData';
import { useShopperId } from '../../../hooks/useShopperId';
import { useLineItemReorderActionStatus } from '../../contexts/LineItemReorderActionStatusContext';
import { useLineItemData } from '../../contexts/LineItemDataContext';
import {
  DOC_CONVERSION_FAILURE,
  EDIT_ACTION,
} from '../../../constants/newRelic';
import { convertLegacyDocument } from '../../../apis/oreosApi';
import {
  docRemediationModalPresentedToCustomer,
  trackDocumentConversionRemediationPresentedToCustomer,
  trackDocumentEditClicked,
  trackFetchDocumentUrlError,
  trackLegacyEditDocumentUrlConversionSuccess,
} from '../../../apis/newrelic/newRelicTrackingApi';
import { BUY_IT_AGAIN_SOURCE_PAGE } from '../../../constants/sourcePage';
import { trackBuyItAgainEditDesignAndReorderClicked } from '../../../apis/trackingApi';

interface ItemEditDesignAndReorderCtaProps {
  buttonWidth?: 'full-width' | 'standard';
  skin?: 'link' | 'primary' | 'secondary';
}

export const ItemEditDesignAndReorderCta = (
  props: ItemEditDesignAndReorderCtaProps
) => {
  const { buttonWidth, skin } = props;
  const [orderHistoryMessages = {}, sharedActionMessages = {}] =
    useTranslations(['orderHistoryMessages', 'sharedActionMessages']);

  const item = useLineItemData();
  const {
    id,
    orderNumber,
    states: itemStates,
    editDocumentUrl,
    productName,
  } = item;

  const shopperId = useShopperId();

  const {
    setDocConversionFailure,
    setGenericFailure,
    setAltDocId,
    resetReorderActionFeedback,
  } = useLineItemReorderActionStatus();

  const { sourcePage } = useSourcePageContext();

  const { tenantEnvironment, vistacartTenant: tenant } = useSiteData();

  const [awaitingApiResponse, setAwaitingApiResponse] = useState(false);

  const applyLinkSkinToButton = 'link' === skin;

  if (!id) {
    return null;
  }

  // call OREOs and handle an error in the case of a DOC_CONVERSION_FAILURE (we couldn't map the legacy item to an item on the new platform)
  const fetchEditDocUrlFromOreos = async () => {
    setAwaitingApiResponse(true);
    // more tracking
    const analyticsMetadata = {
      action: EDIT_ACTION,
      lineItemId: item.id,
      orderNumber,
      shopperId,
      sourcePage,
      tenant,
    };
    try {
      const data = await convertLegacyDocument({
        lineItemId: id,
        orderNumber,
        shopperId,
        sourcePage,
        tenant,
        tenantEnvironment,
      });
      setAwaitingApiResponse(false);
      trackLegacyEditDocumentUrlConversionSuccess(item, sourcePage);
      // track edit doc url successfully retrieved from docGateway
      if (data.successful) {
        return data.docGatewayDesignDetails?.editUrl;
      }
      if (data.failureReason === DOC_CONVERSION_FAILURE) {
        // there was a doc conversion failure, lets pop that modal!
        setDocConversionFailure(true);
        setAltDocId(data.altDocId);
        docRemediationModalPresentedToCustomer(analyticsMetadata);
        trackDocumentConversionRemediationPresentedToCustomer(
          analyticsMetadata
        );
      } else {
        // handle generic error
        setGenericFailure(true);
      }
    } catch (err) {
      // handle error
      if (err instanceof Error) {
        trackFetchDocumentUrlError(err, analyticsMetadata);
      }
      setAwaitingApiResponse(false);
      setGenericFailure(true);
    }
  };

  // This function is called when a user clicks on the "Edit design and reorder" button
  const editDesignAndReorderOnClickHandler = async () => {
    resetReorderActionFeedback(EDIT_ACTION);

    // tracking data for metrics
    const trackingData = {
      editDocumentUrl: editDocumentUrl || 'Legacy Item',
      itemStates,
      orderNumber,
      page: sourcePage,
      vistacartTenantName: tenant,
    };
    trackDocumentEditClicked(trackingData);
    // This was requested for the "Buy It Again" Experiment only
    if (sourcePage === BUY_IT_AGAIN_SOURCE_PAGE) {
      trackBuyItAgainEditDesignAndReorderClicked();
    }
    // if editDocUrl is undefined we call OREOs to get the editDocUrl for a legacy item
    if (!editDocumentUrl || editDocumentUrl === '') {
      /* make call to OREOs to get editDocUrl. If there is an error (i.e. a DOC_CONVERSION_FAILURE) handle that and present modal */
      const editUrl = await fetchEditDocUrlFromOreos();

      if (editUrl) {
        // navigate to edit Document Url for legacy item returned from call to OREOs
        window.location.href = editUrl;
      }
    } else {
      window.location.href = editDocumentUrl;
    }
  };

  if (applyLinkSkinToButton) {
    return (
      <Link
        render={(p) => (
          <Button
            onClick={editDesignAndReorderOnClickHandler}
            disabled={awaitingApiResponse}
            fontWeight="bold"
            className={p.className}
            skin="link"
          >
            {p.children}
          </Button>
        )}
      >
        {awaitingApiResponse && (
          <FlexBox justifyContent="center">
            <Spinner
              accessibleText={sharedActionMessages?.['loading']}
              aria-busy="true"
              aria-live="polite"
              role="status"
              size="standard"
            />
          </FlexBox>
        )}
        {orderHistoryMessages.editButtonText}
        <VisuallyHidden>{`: ${productName}`}</VisuallyHidden>
      </Link>
    );
  }

  return (
    <Button
      onClick={editDesignAndReorderOnClickHandler}
      disabled={awaitingApiResponse}
      width={buttonWidth}
    >
      {awaitingApiResponse && (
        <FlexBox justifyContent="center">
          <Spinner
            accessibleText={sharedActionMessages?.['loading']}
            aria-busy="true"
            aria-live="polite"
            role="status"
            size="standard"
          />
        </FlexBox>
      )}
      {/* This padding prevents the button from resizing when the preloader appears/disappears */}
      <Span mx={awaitingApiResponse ? 0 : 3}>
        {orderHistoryMessages.editDesignAndReorderButtonText}
      </Span>
      <VisuallyHidden>{`: ${productName}`}</VisuallyHidden>
    </Button>
  );
};
